/**
 * Chart themes (screenshots/80 theme builder). A theme is our own small model; `toEChartsTheme()` turns it
 * into an Apache ECharts theme object for `echarts.registerTheme(id, …)`, and ECharts 6's
 * `chart.setTheme(id)` recolors live charts in place (no re-init).
 *
 * Fixed-meaning colors are NOT themeable: support/oppose (positive/negative), forecast for/toss-up/against,
 * and Live always come from the design tokens, so a supporter is green and a prediction looks like a
 * prediction in every theme. `paletteFromTheme()` enforces that by only overriding the neutral slots.
 */
import type { Palette } from "./palette";
import { checkCategorical, checkSequential, isHex, worstState, type Check } from "./paletteChecks";

export type ThemeScope = "builtin" | "team" | "user";
export const MAX_THEME_COLORS = 6;

export interface ChartTheme {
  id: string;                         // slug, also the ECharts theme name
  name: string;
  description?: string;
  scope: ThemeScope;
  basedOn?: string;
  palette: string[];                  // category colors, 2–6, hex
  names?: string[];                   // a name per category color ("Primary", "Accent", …; screenshots/80)
  sequential: [string, string, string, string];
  diverging: [string, string, string];   // negative · middle · positive
  text: string;
  textMuted: string;
  grid: string;
  font: string;
  fontSize: number;                   // label size
  barRadius: number;
  lineWidth: number;
  showSymbols: boolean;
  tooltip: "light" | "dark";
  legendIcon: "circle" | "rect" | "roundRect";
  legendPosition: "top" | "bottom" | "right";
  motion: { duration: number; easing: "cubicOut" | "quarticOut" | "linear" };
  updatedAt?: string;
  updatedBy?: string;                 // who saved last (names the other editor on a conflict, 90)
}

const base = {
  text: "#1F2A37", textMuted: "#6B7380", grid: "#E6EDF5", font: "Inter", fontSize: 12, barRadius: 3, lineWidth: 2, showSymbols: false,
  tooltip: "light", legendIcon: "circle", legendPosition: "bottom", motion: { duration: 300, easing: "cubicOut" },
} as const;

export const BUILTIN_THEMES: ChartTheme[] = [
  { ...base, id: "civic-light", name: "Civic Light", scope: "builtin", description: "Default for new charts",
    palette: ["#2B5F9E", "#B4651C", "#6B4FA0"], sequential: ["#93B4E0", "#6390CC", "#3A6CB3", "#1F4A85"], diverging: ["#B4651C", "#EEF1F4", "#2B5F9E"] },
  { ...base, id: "high-contrast", name: "High contrast", scope: "builtin", description: "For projectors and low vision",
    palette: ["#1F4A85", "#9A4A0E", "#4B3478"], sequential: ["#93B4E0", "#6390CC", "#3A6CB3", "#1F4A85"], diverging: ["#9A4A0E", "#EEF1F4", "#1F4A85"],
    text: "#000000", textMuted: "#3A4450", fontSize: 13, lineWidth: 3, showSymbols: true },
  { ...base, id: "print", name: "Print · grayscale-safe", scope: "builtin", description: "Reads in black-and-white print",
    palette: ["#1F2A37", "#5B6573", "#9AA3AE"], sequential: ["#C9CED4", "#9AA3AE", "#5B6573", "#1F2A37"], diverging: ["#5B6573", "#F1F3F5", "#1F2A37"],
    showSymbols: true },
];

/** Checks shown in the editor; the Save button stays enabled but warns while any check fails. */
export function validateTheme(t: ChartTheme): { categorical: Check[]; sequential: Check[]; issues: string[]; state: "pass" | "warn" | "fail" } {
  const issues: string[] = [];
  if (!t.name.trim()) issues.push("Give the theme a name.");
  if (t.palette.length < 2 || t.palette.length > MAX_THEME_COLORS) issues.push(`Use 2–${MAX_THEME_COLORS} category colors.`);
  const allHex = [...t.palette, ...t.sequential, ...t.diverging, t.text, t.textMuted, t.grid];
  const bad = allHex.filter((c) => !isHex(c));
  if (bad.length) issues.push(`Not a 6-digit hex color: ${bad.join(", ")}`);
  if (bad.length) return { categorical: [], sequential: [], issues, state: "fail" };
  const categorical = checkCategorical(t.palette, { surface: "#FFFFFF" });
  const sequential = checkSequential(t.sequential, { surface: "#FFFFFF" });
  const state = issues.length ? "fail" : [worstState(categorical), worstState(sequential)].includes("fail") ? "fail"
    : [worstState(categorical), worstState(sequential)].includes("warn") ? "warn" : "pass";
  return { categorical, sequential, issues, state };
}

/** Our theme → ECharts theme object (the same shape the ECharts Theme Builder exports). */
export function toEChartsTheme(t: ChartTheme): Record<string, unknown> {
  const axis = {
    axisLine: { show: false }, axisTick: { show: false },
    axisLabel: { color: t.textMuted, fontSize: t.fontSize - 1 },
    splitLine: { lineStyle: { color: t.grid } }, nameTextStyle: { color: t.textMuted },
  };
  const tooltipDark = t.tooltip === "dark";
  return {
    color: t.palette,
    backgroundColor: "transparent",
    textStyle: { fontFamily: t.font, color: t.text, fontSize: t.fontSize },
    title: { textStyle: { color: t.text, fontWeight: 600 }, subtextStyle: { color: t.textMuted } },
    legend: { icon: t.legendIcon, textStyle: { color: t.textMuted } },
    tooltip: { backgroundColor: tooltipDark ? "#1A1A1A" : "#FFFFFF", borderColor: tooltipDark ? "#1A1A1A" : t.grid, textStyle: { color: tooltipDark ? "#FFFFFF" : t.text } },
    categoryAxis: axis, valueAxis: axis, logAxis: axis, timeAxis: axis,
    bar: { itemStyle: { borderRadius: t.barRadius } },
    line: { lineStyle: { width: t.lineWidth }, symbol: t.showSymbols ? "circle" : "none", symbolSize: 6 },
    radar: { lineStyle: { width: t.lineWidth }, symbol: t.showSymbols ? "circle" : "none" },
    graph: { lineStyle: { color: t.grid } },
    visualMap: { inRange: { color: [...t.sequential] } },
    animationDuration: t.motion.duration, animationDurationUpdate: t.motion.duration, animationEasing: t.motion.easing, animationEasingUpdate: t.motion.easing,
  };
}

/**
 * Import from the ECharts Theme Builder (echarts.apache.org/en/theme-builder). Accepts either the
 * builder's config export (`{ themeName, theme: { color, visualMapColor, textColor, … } }`) or a plain
 * ECharts theme object (`{ color, visualMap, textStyle, … }`). Unknown fields are ignored; the result
 * still goes through `validateTheme` before it can be saved.
 */
export function fromEChartsTheme(json: unknown, o: { id: string; name?: string; scope?: ThemeScope }): ChartTheme {
  const j = (json ?? {}) as Record<string, any>;               // eslint-disable-line @typescript-eslint/no-explicit-any
  const t = (j.theme && typeof j.theme === "object" ? j.theme : j) as Record<string, any>;   // eslint-disable-line @typescript-eslint/no-explicit-any
  const hexes = (a: unknown) => (Array.isArray(a) ? a.filter((c): c is string => typeof c === "string" && isHex(c)).map((c) => c.toUpperCase()) : []);
  const base = BUILTIN_THEMES[0];
  const palette = hexes(t.color).slice(0, MAX_THEME_COLORS);
  const seqIn = hexes(t.visualMapColor ?? t.visualMap?.inRange?.color);
  const seq = seqIn.length >= 2 ? resample(seqIn, 4) : [...base.sequential];
  const text = [t.textColor, t.textStyle?.color].find((c) => typeof c === "string" && isHex(c)) ?? base.text;
  return {
    ...base, id: o.id, scope: o.scope ?? "user", basedOn: undefined,
    name: o.name ?? (typeof j.themeName === "string" ? j.themeName : "Imported theme"),
    palette: palette.length >= 2 ? palette : [...base.palette],
    sequential: seq as ChartTheme["sequential"],
    diverging: [palette[1] ?? base.diverging[0], "#EEF1F4", palette[0] ?? base.diverging[2]],
    text,
    font: typeof t.textStyle?.fontFamily === "string" ? t.textStyle.fontFamily.split(",")[0].replace(/['"]/g, "").trim() : base.font,
  };
}

/** Pick n colors spread across a list (keeps both ends). */
function resample(cs: string[], n: number) {
  return Array.from({ length: n }, (_, i) => cs[Math.round((i * (cs.length - 1)) / (n - 1))]);
}

/** The default chart theme: charts using it take the dashboard theme's chart colors (Settings → Appearance), not its fixed palette. */
export const UI_CHART_THEME = "civic-light";

/** Merge a theme into the token palette used by buildOption. Only neutral slots change; meaning colors stay. The default theme keeps the UI palette. */
export function paletteFromTheme(p: Palette, t: ChartTheme): Palette {
  if (t.id === UI_CHART_THEME) return p;
  return {
    ...p,
    c1: t.palette[0] ?? p.c1, c2: t.palette[1] ?? p.c2, c3: t.palette[2] ?? p.c3,
    seq: [...t.sequential] as Palette["seq"],
    text: t.text, textMuted: t.textMuted, grid: t.grid, font: t.font,
    // positive / negative / forecast* / tossup are deliberately NOT taken from the theme
  };
}

/** Snippet shown in the theme builder's "Use in ECharts" card. */
export const themeSnippet = (t: ChartTheme) =>
  `echarts.registerTheme('${t.id}', ${JSON.stringify(toEChartsTheme(t), null, 2)});\necharts.init(el, '${t.id}');`;

export const slugify = (name: string) => name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "theme";

// ------------------------------------------------------------------ two editors (screenshots/90)
/** One slot changed differently by both people. `slot` is a path like `palette.2`, `sequential.0`, `barRadius`. */
export interface ThemeConflict { slot: string; label: string; mine: unknown; theirs: unknown }
export interface ThemeMerge { merged: ChartTheme; conflicts: ThemeConflict[]; fromTheirs: string[] }

const SCALARS = ["name", "description", "text", "textMuted", "grid", "font", "fontSize", "barRadius", "lineWidth", "showSymbols", "tooltip", "legendIcon", "legendPosition"] as const;
const SLOT_LABEL: Record<string, string> = { name: "Name", description: "Description", text: "Text color", textMuted: "Muted text", grid: "Grid lines", font: "Font",
  fontSize: "Label size", barRadius: "Bar corners", lineWidth: "Line width", showSymbols: "Line points", tooltip: "Tooltip", legendIcon: "Legend icon",
  legendPosition: "Legend position", "motion.duration": "Motion duration", "motion.easing": "Motion easing", palette: "Category colors" };
const DIVERGING = ["negative", "middle", "positive"];

export function slotLabel(slot: string): string {
  const [a, b] = slot.split(".");
  if (a === "palette" && b !== undefined) return `Color ${Number(b) + 1}`;
  if (a === "sequential") return `Sequential step ${Number(b) + 1}`;
  if (a === "diverging") return `Diverging ${DIVERGING[Number(b)]}`;
  return SLOT_LABEL[slot] ?? slot;
}

function flatten(t: ChartTheme, perColor: boolean): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const k of SCALARS) o[k] = t[k];
  o["motion.duration"] = t.motion.duration;
  o["motion.easing"] = t.motion.easing;
  if (perColor) t.palette.forEach((c, i) => { o[`palette.${i}`] = c; }); else o.palette = t.palette;
  t.sequential.forEach((c, i) => { o[`sequential.${i}`] = c; });
  t.diverging.forEach((c, i) => { o[`diverging.${i}`] = c; });
  return o;
}

function setSlot(t: ChartTheme, slot: string, v: unknown) {
  const [a, b] = slot.split(".");
  const rec = t as unknown as Record<string, unknown>;
  if (b === undefined) rec[a] = structuredClone(v);
  else if (a === "motion") (t.motion as unknown as Record<string, unknown>)[b] = v;
  else (rec[a] as unknown[])[Number(b)] = v;
}

const norm = (v: unknown) => JSON.stringify(v, (_k, x) => (typeof x === "string" && /^#[0-9a-f]{3,8}$/i.test(x) ? x.toUpperCase() : x));
const same = (a: unknown, b: unknown) => norm(a) === norm(b);

/**
 * Three-way merge, slot by slot: a change only one side made is kept; the same change on both sides is fine;
 * different changes to the same slot are returned as conflicts (the merged theme keeps "mine" until resolved).
 * If either side added or removed category colors, the palette is compared as one slot.
 */
export function mergeThemes(base: ChartTheme, mine: ChartTheme, theirs: ChartTheme): ThemeMerge {
  const perColor = base.palette.length === mine.palette.length && mine.palette.length === theirs.palette.length;
  const B = flatten(base, perColor), M = flatten(mine, perColor), T = flatten(theirs, perColor);
  const merged: ChartTheme = { ...structuredClone(mine), updatedAt: theirs.updatedAt, updatedBy: theirs.updatedBy };
  const conflicts: ThemeConflict[] = [], fromTheirs: string[] = [];
  for (const k of Object.keys(B)) {
    if (same(B[k], T[k]) || same(M[k], T[k])) continue;        // they didn't change it, or we agree
    if (same(B[k], M[k])) { setSlot(merged, k, T[k]); fromTheirs.push(slotLabel(k)); continue; }
    conflicts.push({ slot: k, label: slotLabel(k), mine: M[k], theirs: T[k] });
  }
  return { merged, conflicts, fromTheirs };
}

/** Apply the user's picks ("Yours" is the default for anything not picked). Re-run validateTheme afterwards. */
export function resolveThemeConflicts(m: ThemeMerge, keep: Record<string, "mine" | "theirs">): ChartTheme {
  const t = structuredClone(m.merged);
  for (const c of m.conflicts) if (keep[c.slot] === "theirs") setSlot(t, c.slot, c.theirs);
  return t;
}
